/**
 * Server calls. Note what has no function here: uploading a template or an
 * embedding. There is no endpoint, and the server rejects one at the byte level.
 */

import { osVersionFromUserAgent } from "./os.js";

let token = null;

/**
 * The client's identity, sent on every request as `X-MeetFolks-Client`.
 *
 * The version is the build id rather than a hand-kept semver. It is the one value that
 * is actually per-build: `lib/claims.js` already stamps it into every signed payload for
 * the same reason, and a deploy that changed the app without bumping a version number
 * would otherwise be invisible. The server never gates the web -- it is served from the
 * same release as the server -- so this header is what identifies the build in the
 * server's logs (#475, R62).
 *
 * Read from `globalThis` at call time, like `lib/claims.js`: the symbol is assigned by
 * `main.tsx` after this module loads, and a module-scope read would capture `undefined`.
 */
const CLIENT_PLATFORM = "web";
export const CLIENT_HEADER_NAME = "X-MeetFolks-Client";
export function clientHeader() {
  return `${CLIENT_PLATFORM}/${globalThis.__MEETFOLKS_BUILD__ ?? "dev"}`;
}

/**
 * The exact build a request came from, sent beside `X-MeetFolks-Client` as
 * `X-MeetFolks-Build` (#980): `build=<BUILD_ID>; commit=<sha8>; os=<family> <major>`.
 *
 * `X-MeetFolks-Client` stays the coarse platform/version pair the min-version gate reads;
 * this is the per-build detail a meeting's device rows record. `build` is the same build id
 * `clientHeader` carries, `commit` is the 8-char commit injected at build time, and `os` is
 * the browser-reported family and major when the user agent admits one. Keys are omitted
 * when their value is unknown; the server reads a missing key as `unknown`. Every value is
 * held to the wire's `[A-Za-z0-9._ -]`, and a value that misses it drops its key rather
 * than send it.
 */
export const BUILD_HEADER_NAME = "X-MeetFolks-Build";
const BUILD_VALUE = /^[A-Za-z0-9._ -]+$/;

function cleanBuildValue(value) {
  const text = String(value ?? "").trim();
  return text && BUILD_VALUE.test(text) ? text : null;
}

export function buildHeader(userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "") {
  const parts = [];
  const build = cleanBuildValue(globalThis.__MEETFOLKS_BUILD__ ?? "dev");
  if (build) parts.push(`build=${build}`);
  const commit = cleanBuildValue(globalThis.__MEETFOLKS_COMMIT__ ?? "unknown");
  if (commit) parts.push(`commit=${commit}`);
  const os = osVersionFromUserAgent(userAgent);
  const osValue = os === "other" ? null : cleanBuildValue(os);
  if (osValue) parts.push(`os=${osValue}`);
  const header = parts.join("; ");
  // The wire caps the whole value at 200 chars; a value that would exceed it is dropped
  // rather than sent truncated, which the server would ignore key by key anyway.
  return header && header.length <= 200 ? header : null;
}

function buildHeaderEntry() {
  const value = buildHeader();
  return value ? { [BUILD_HEADER_NAME]: value } : {};
}

export function setToken(t) {
  token = t;
}

/**
 * Told about every 401, whichever credential was refused (#854). The handler only takes
 * it as a hint to ask `/me` whether the sign-in is still good; see `engine/session-ended.ts`.
 */
let onUnauthorized = null;
export function setUnauthorizedHandler(handler) {
  onUnauthorized = handler;
}

/**
 * Statuses where the server is telling us to come back, not that the request was
 * wrong. 503 is the one that matters: the API answers `OperationalError` with it
 * and a `Retry-After`, meaning the writer was busy, nothing about the request was
 * invalid.
 *
 * [M] Not retrying cost a real session three quarters of a channel. One chunk
 * upload took a 503 while another session was finalising; the client treated it as
 * final and never uploaded audio again, so that device posted claims for all 233 s
 * but audio for only the first 60 s. The merge then attributed 116 s to a speaker
 * whose channel held no audio to transcribe, and those turns came back empty.
 */
const RETRYABLE = new Set([429, 502, 503, 504]);
const MAX_ATTEMPTS = 4;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function request(method, path, body, extra = {}) {
  const headers = {
    ...extra.headers,
    [CLIENT_HEADER_NAME]: clientHeader(),
    ...buildHeaderEntry(),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  let payload = body;
  // `URLSearchParams` travels as the browser's own `application/x-www-form-urlencoded`
  // body -- the meeting email unsubscribe's RFC 8058 body (#864) and nothing else.
  if (body !== undefined && !(body instanceof FormData) && !(body instanceof URLSearchParams)) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  // Opt-in deadline (#38). Only the boot account/link reads pass one: a request that
  // must answer before the app can decide whether to block recording cannot wait for
  // `fetch` to give up on a captive portal. Bulk audio never does, so the timeout is
  // never a ceiling on an upload.
  const signal =
    extra.signal ?? (extra.timeoutMs ? AbortSignal.timeout(extra.timeoutMs) : undefined);

  let res, text;
  const maxAttempts = extra.retry === false ? 1 : MAX_ATTEMPTS;
  for (let attempt = 1; ; attempt++) {
    // `credentials: "same-origin"` is the default, and the account cookie rides
    // on it. Everything here is a relative path -- the worker proxies /v1/* to
    // the API so the whole app is one origin -- which is what lets the sign-in
    // cookie be HttpOnly and SameSite=Lax with no CORS accommodation at all.
    //
    // The `signal` pass-through lets an upstream `AbortController` cancel an
    // in-flight fetch — used by the wrap-up deadline and `onVisible` to abort
    // a stalled tail upload. An abort throws `AbortError` from `fetch`; the
    // `try/catch` normalises it so callers see a consistent error shape with
    // `aborted: true` rather than a raw `AbortError` escaping into the chain.
    try {
      res = await fetch(path, { method, headers, body: payload, signal });
      text = await res.text();
    } catch (err) {
      if (extra.signal?.aborted || signal?.aborted) {
        const e = new Error("request aborted");
        e.aborted = true;
        throw e;
      }
      throw err;
    }
    if (res.ok || !RETRYABLE.has(res.status) || attempt >= maxAttempts) break;
    if (extra.signal?.aborted) break;
    // Honour Retry-After when the server sends one; otherwise back off. Every
    // write endpoint here is idempotent on a natural key -- a chunk by its
    // `local_start_sec`, a claim bundle by its nonce -- so a replayed request
    // replaces rather than duplicates. That is what makes this safe to do blindly.
    const after = Number(res.headers.get("Retry-After"));
    await sleep(Number.isFinite(after) && after > 0 ? after * 1000 : 400 * 2 ** (attempt - 1));
    if (extra.signal?.aborted) break;
  }

  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { detail: text };
  }
  if (!res.ok) {
    const detail = data?.detail ?? res.statusText;
    const err = new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
    err.status = res.status;
    err.data = data;
    // The header a 429 carries is copy-worthy: Home uses it to say when to come back
    // rather than leaving the person to guess (#597). The retry loop above honours it
    // too; this is the final response's value, the one the caller actually saw.
    const after = Number(res.headers.get("Retry-After"));
    if (Number.isFinite(after) && after > 0) err.retryAfter = after;
    if (res.status === 401) onUnauthorized?.();
    throw err;
  }
  // `withStatus` exists because 2xx is not one thing here: 202 means "not yet"
  // and its body is `{detail}`, not the resource. Callers that can receive a 202
  // have to see the status to tell them apart -- see `getResult`.
  if (extra.withHeaders) return { status: res.status, data, headers: res.headers };
  return extra.withStatus ? { status: res.status, data } : data;
}

export const config = (extra = {}) => request("GET", "/v1/config", undefined, extra);

export const registerDevice = (body) => request("POST", "/v1/devices", body);
export const updateCapabilities = (caps) => request("PATCH", "/v1/devices/me", caps);
/** This device as the server knows it; a 401 means its token is no longer accepted. */
export const deviceMe = () => request("GET", "/v1/devices/me");
/**
 * Exchange this device's token for a fresh one; the old token stops working as soon as
 * the server commits (#436). No timeout on purpose: aborting after the commit would lose
 * the only copy of the new token.
 */
export const rotateDeviceToken = () => request("POST", "/v1/devices/me/token");

/**
 * Flush a batch of client telemetry events.
 *
 * ``keepalive`` so the final flush of a session survives the page being torn
 * down (a tab closed mid-session, a reload). The body is identifiers and counts
 * only, never transcript text -- see `web/lib/telemetry.js`.
 */
export async function postTelemetry(body) {
  const headers = {
    "Content-Type": "application/json",
    [CLIENT_HEADER_NAME]: clientHeader(),
    ...buildHeaderEntry(),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch("/v1/telemetry", {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    keepalive: true,
  });
  if (!res.ok) throw new Error(`telemetry ${res.status}`);
}

/**
 * Capture the server half of the additive four-timestamp clock-sync shadow.
 * The caller adds its t4 receive reading after this JSON response resolves.
 */
// A retry would pair one request's t1 with a later request's t2/t3 and make a
// failed attempt look like an implausibly long but valid network interval.
export const postClockSync = (body) =>
  request("POST", "/v1/telemetry/clock-sync", body, { retry: false });

// -- accounts ----------------------------------------------------------
//
// Note which credential each of these travels on. The device token authenticates
// the recording path and rides in the Authorization header; the account cookie
// authenticates history, the device list and revocation, and the page never sees
// it -- it is HttpOnly, so `document.cookie` cannot read it and neither can an
// injected script. `linkDevice` is the one call that needs both.

export const sendMagicLink = (email) => request("POST", "/v1/accounts/magic-link", { email });
export const verifyMagicLink = (token) =>
  request("POST", "/v1/accounts/magic-link/verify", { token });
// The store reviewers' code (#800). Not retried: every attempt counts against the
// per-network limit, and a 429 retried would only spend more of it.
export const signInWithReviewCode = (email, code) =>
  request("POST", "/v1/accounts/review-code", { email, code }, { retry: false });

/**
 * The signed-in account, or `null`.
 *
 * 401 is the ordinary answer for a signed-out device, not an error worth
 * surfacing -- most people never sign in at all, because recording does not need
 * an account.
 */
export async function account(extra = {}) {
  try {
    return await request("GET", "/v1/accounts/me", undefined, extra);
  } catch (err) {
    // Only a deliberate refusal is "signed out". A transport failure is thrown so the
    // caller cannot read "we could not ask" as "nobody is signed in" (#38).
    if (err.status === 401) return null;
    throw err;
  }
}

export const signOut = () => request("POST", "/v1/accounts/logout");

/**
 * Agree to how MeetFolks uses AI (#915), naming the wording `version` that was shown.
 * Answers the account, now current; a 409 carries `code: ai_consent_version_stale`.
 */
export const agreeToAiUse = (version) =>
  request("POST", "/v1/accounts/me/ai-consent", { version });

/**
 * Turn the account's meeting emails on or off (#864). Answers the account, now
 * current. Idempotent on the server, so a double tap or a retry settles the same way.
 */
export const setEmailMeetings = (enabled) =>
  request("POST", "/v1/accounts/me/email-meetings", { enabled });

/**
 * Spend a meeting email's one-click unsubscribe token (#864).
 *
 * The RFC 8058 body, exactly as a mailbox provider would send it, so the page and the
 * provider's own Unsubscribe button speak one protocol. 204 on success; a 404 when the
 * token names no account, which the page reads as `bad_link` and nothing else.
 */
export const unsubscribeFromMeetingEmails = (token) =>
  request(
    "POST",
    `/v1/email/unsubscribe/${encodeURIComponent(token)}`,
    new URLSearchParams({ "List-Unsubscribe": "One-Click" }),
    { retry: false },
  );

// Deleting an account (#781). None of the three is retried: the deletion is not one to
// repeat behind the person's back, and every delete-request attempt sends a mail and
// counts against the per-address and per-network limits.
/** Delete the signed-in account now. 202 on success; a 409 carries `code: account_in_live_meeting`. */
export const deleteAccount = () =>
  request("DELETE", "/v1/accounts/me", undefined, { retry: false });
/** Mail a deletion link to `email`. The same 202 whether or not it has an account. */
export const requestAccountDeletion = (email) =>
  request("POST", "/v1/accounts/delete-request", { email }, { retry: false });
/** Spend an emailed deletion link's token and delete its account. 400 when expired or used. */
export const confirmAccountDeletion = (token) =>
  request("POST", "/v1/accounts/delete-confirm", { token }, { retry: false });
export const linkDevice = (extra = {}) =>
  request("POST", "/v1/accounts/me/devices/link", undefined, extra);
export const revokeDevice = (claimProfileId) =>
  request("POST", `/v1/accounts/me/devices/${encodeURIComponent(claimProfileId)}/revoke`);
/**
 * The account's session history, newest first.
 *
 * Paged by `limit`/`offset`: home asks for the first two, the sessions screen
 * pages through the rest. Both are optional -- omitting them is the server's
 * default page, which is what the account screen used before the list moved.
 */
export const accountSessions = async ({ limit, offset } = {}) => {
  const params = new URLSearchParams();
  if (limit != null) params.set("limit", limit);
  if (offset != null) params.set("offset", offset);
  const query = params.toString();
  const res = await request("GET", `/v1/accounts/me/sessions${query ? `?${query}` : ""}`, undefined, {
    withHeaders: true,
  });
  const totalHeader = res.headers?.get("x-total-count");
  const total = totalHeader != null && totalHeader !== "" ? Number(totalHeader) : null;
  const items = Array.isArray(res.data) ? res.data : [];
  items.total = Number.isFinite(total) ? total : null;
  return items;
};
/**
 * The signed-in host's meeting time: balance, recent history, the packs on sale, and
 * whether a new meeting would start right now (#686). Read-only.
 */
export const wallet = () => request("GET", "/v1/billing/wallet");
/**
 * One page of the signed-in host's whole credits ledger, newest first, paged like
 * `accountSessions`: the rows, with the ledger's row count as `.total` (from
 * `X-Total-Count`), or null when the header is missing.
 */
export const walletTransactions = async ({ limit, offset } = {}) => {
  const params = new URLSearchParams();
  if (limit != null) params.set("limit", limit);
  if (offset != null) params.set("offset", offset);
  const query = params.toString();
  const res = await request("GET", `/v1/billing/transactions${query ? `?${query}` : ""}`, undefined, {
    withHeaders: true,
  });
  const totalHeader = res.headers?.get("x-total-count");
  const total = totalHeader != null && totalHeader !== "" ? Number(totalHeader) : null;
  const items = Array.isArray(res.data) ? res.data : [];
  items.total = Number.isFinite(total) ? total : null;
  return items;
};
/** Start Dodo's checkout for one pack; the answer's `checkout_url` is where to go and pay. */
export const checkout = (pack) => request("POST", "/v1/billing/checkout", { pack }, { retry: false });
/**
 * Monthly plans on the web (#875). Each is one attempt, never retried: a retried checkout
 * or plan change could start or charge twice. A refusal carries the server's code in
 * `data.detail.code` (`subscription_exists`, `subscription_not_changeable`, ...).
 */
export const planCheckout = (plan) =>
  request("POST", "/v1/billing/plan-checkout", { plan }, { retry: false });
/** Upgrade now or downgrade at the renewal: `{ plan, effective: "now" | "renewal" }`. */
export const changePlan = (plan) =>
  request("POST", "/v1/billing/subscription/change", { plan }, { retry: false });
/** What an upgrade would charge today: `{ plan, amount_minor, currency }`, nulls when unknown. */
export const previewPlanChange = (plan) =>
  request("POST", "/v1/billing/subscription/change/preview", { plan }, { retry: false });
/** Dodo's customer portal for the web plan: `{ url }`. */
export const subscriptionPortal = () =>
  request("POST", "/v1/billing/subscription/portal", undefined, { retry: false });

/** Start a meeting. It carries no name: the summary or its creator names it later (#708). */
export const createSession = () => request("POST", "/v1/sessions", {});
/**
 * Rename a meeting (#708). Only its creator may: anyone else in it gets 403, anyone
 * outside it 404. No retry: the rename screen puts the old name back and says so at once,
 * rather than waiting out a backoff while the new name shows.
 */
export const renameSession = (sessionId, title) =>
  request("PATCH", `/v1/sessions/${sessionId}/title`, { title }, { retry: false });
/** Rename one anonymous speaker for this session; the server returns the projected result. */
export const renameSpeaker = (sessionId, speaker, name) =>
  request(
    "PATCH",
    `/v1/sessions/${sessionId}/speakers/${encodeURIComponent(speaker)}`,
    { name },
    { retry: false },
  );
export const joinSession = (joinCode, claimProfileId, joinMethod) =>
  request("POST", "/v1/sessions/join", {
    join_code: joinCode,
    claim_profile_id: claimProfileId,
    join_method: joinMethod,
  });
/**
 * Mint the short-lived browser capability for the development-only M0 stream probe.
 * `request` adds the in-memory device Authorization header; the caller receives only the
 * signed ticket and never needs to read or copy the bearer token.
 */
export const issueStreamTicket = (sessionId) =>
  request("POST", "/v1/stream/ticket", { session_id: sessionId });

export const getSession = (id) => request("GET", `/v1/sessions/${id}`);

/**
 * The producer lease this device must quote on every continuous claim delta (#170-A).
 *
 * Advisory by construction: it can go stale between this call and the write that quotes
 * it, and the append route -- not this lookup -- decides. `retry: false` because the
 * caller's recovery for a refusal is to re-read the lease, not to replay the read: a
 * blind 503 retry here would spend the delay budget of the write it is holding up.
 */
export const getClaimDeltaContext = (id, options = {}) =>
  request("GET", `/v1/sessions/${id}/claim-deltas/context`, undefined, {
    retry: false,
    // Cancellable for the same reason the marker is (#170-B): the readiness loop that
    // decides this recording's payload protocol awaits this call, and a request that
    // never settles holds that decision open for the whole conversation -- silently,
    // because the session otherwise looks healthy.
    signal: options.signal,
  });

/**
 * Post one signed continuous claim delta.
 *
 * `delta` carries the exact signed payload bytes plus the outer producer metadata, and
 * is transmitted verbatim -- the caller has already fixed the bytes, and nothing here
 * may reshape them. Safe under the blind retry above because the ledger is idempotent on
 * `(timeline_epoch, delta_id)`: a replay of a request that already committed answers
 * `duplicate`, not a second row.
 */
export const postClaimDelta = (id, delta) =>
  request("POST", `/v1/sessions/${id}/claim-deltas`, delta);

/**
 * Ask what the server actually holds, after an answer this device never saw.
 *
 * This is what makes a timeout survivable: the high-water mark it returns is the only
 * way to tell "the write landed and the response was lost" from "the write never
 * landed", and those two need opposite actions from the queue.
 */
export const resumeClaimDeltas = (id, body) =>
  request("POST", `/v1/sessions/${id}/claim-deltas/resume`, body);

/**
 * Declare that this device is streaming claim deltas for one timeline epoch (#170-B).
 *
 * The server's stop barrier counts activated devices, so this is what makes a device that
 * produced *nothing* still something the merge has to wait for. Without it, a device that
 * activated and then crashed is indistinguishable from a legacy device with nothing to
 * say, and the merge proceeds without it.
 *
 * Safe under the blind retry `request` performs: the route is idempotent on
 * `(session, device, epoch)` and answers an exact replay `duplicate` rather than creating
 * a second row.
 */
export const activateClaimDeltas = (id, body, options = {}) =>
  request("POST", `/v1/sessions/${id}/claim-deltas/activate`, body, {
    withStatus: true,
    signal: options.signal,
    // `retry: false`, and this is a correctness point rather than a preference (#170-B).
    // `request`'s internal retry sleeps on `Retry-After` -- up to the server's own number
    // of seconds -- and those sleeps are not interruptible by the caller's signal, so a
    // 503 with a generous `Retry-After` would outlive the outer deadline and hold the
    // protocol decision open for exactly as long as the retry budget allowed. The gate
    // is a state machine that owns its own bounded, abortable retries; two retry ladders
    // over one idempotent request means the outer bound is not a bound.
    retry: false,
  });

/**
 * Publish the durable proof that this device has sent its last claim delta (#170-B).
 *
 * `body` is transmitted verbatim and **must not be rebuilt between attempts**. It carries
 * the exact writer ACK pair -- the sequence and digest the server already acknowledged --
 * and the server accepts it only if that pair equals the committed ledger head. Recomputing
 * it on a retry is the one thing that turns a recoverable timeout into an unrecoverable
 * disagreement: the first request may have committed, and a second body describing a
 * different pair can then never be reconciled with it.
 *
 * `withStatus` because 200 carries two outcomes the caller has to tell apart for reporting
 * -- `accepted` and `duplicate` -- and both are success.
 */
export const postClaimDeltaFinalWatermark = (id, body, options = {}) =>
  request("POST", `/v1/sessions/${id}/claim-deltas/final-watermark`, body, {
    withStatus: true,
    // The caller's abort signal, so a request into a network black hole can actually be
    // cancelled rather than merely abandoned (#170-B). `request` already threads this to
    // `fetch` and normalises the resulting `AbortError`. Without it the promise never
    // settles and no attempt bound helps: a bound counts attempts, and an attempt that
    // never ends is never counted.
    signal: options.signal,
  });
// Announce that the conversation is over, without claiming to have finished sending.
// Separate from `finaliseSession` so it can be called *before* the scoring pass and
// the audio upload -- those took 24 s on a real session, and until they finished
// nobody else knew to stop. See the `close` endpoint.
export const closeSession = (id) => request("POST", `/v1/sessions/${id}/close`);
export const finaliseSession = (id) => request("POST", `/v1/sessions/${id}/finalise`);
export const leaveSession = (id) => request("POST", `/v1/sessions/${id}/leave`);
/**
 * Leave a meeting this page was never in: Leave it, from Home (#742). This device's
 * earlier page or app may have died mid-meeting with claims still open, and nothing
 * can publish their final watermark now, so the server closes them at the claims it
 * already has. Never from the meeting's own wrap-up: that page publishes its marker.
 */
export const leaveOtherMeeting = (id) =>
  request("POST", `/v1/sessions/${id}/leave?abandon_open_claims=true`);
/** A start called off before any audio: no meeting, nothing in history (#663). Creator only. */
export const abandonSession = (id) => request("POST", `/v1/sessions/${id}/abandon`);
/**
 * End a meeting this account pays for, from any of its devices, member or not (#729):
 * the host's End, done from here. Answers `{session_id, state}` only. No retry: a 429
 * is per device and long, so Home says when to come back instead of waiting it out.
 */
export const endSession = (id) =>
  request("POST", `/v1/sessions/${id}/end`, undefined, { retry: false });

/**
 * Stream real-time SSE session events for instant multi-device coordination.
 * Passes Bearer authorization token and reads streaming response.
 *
 * Automatically reconnects on unexpected drops (deploy, network blip) so
 * the device stays subscribed through a Railway rollout. Does NOT reconnect
 * when the caller intentionally aborts via `signal`.
 *
 * Reconnects are stateless: the events route carries no replay buffer (the
 * live-transcript feed that needed one is gone, #361), so there is no cursor
 * to resume from -- a reconnecting client re-reads state on its next poll.
 *
 * `onConnectionChange` reports stream health so the caller can demote its own
 * fallback polling while this stream is up (#440): true only after the server's
 * initial `connected` event is parsed, false on a drop, refusal or abort. The second
 * argument gives bounded connection metadata; the event callback receives
 * reconnect count and backoff only for a `connected` event.
 */
export function streamSessionEvents(id, onEvent, signal, onConnectionChange) {
  let retryMs = 1000;
  const MAX_RETRY_MS = 30000;
  let authorizationRevoked = false;
  let reconnectCount = 0;
  let reconnectDelayMs = null;

  function connect() {
    if (signal.aborted || authorizationRevoked) return;
    // Read per connect, not once per stream: a token rotated mid-recording (#436),
    // here or in another tab, must reach the reconnect.
    const headers = { [CLIENT_HEADER_NAME]: clientHeader(), ...buildHeaderEntry() };
    if (token) headers.Authorization = `Bearer ${token}`;
    fetch(`/v1/sessions/${id}/events`, { headers, signal })
      .then(async (res) => {
        if (!res.ok || !res.body) {
          onConnectionChange?.(false, {
            reason: "http_error",
            status: res.status,
            reconnectCount,
          });
          return;
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const blocks = buffer.split("\n\n");
          buffer = blocks.pop() || "";

          for (const block of blocks) {
            for (const line of block.split("\n")) {
              if (line.startsWith("data: ")) {
                try {
                  const data = JSON.parse(line.slice(6));
                  if (data.event === "connected") {
                    // A stream is healthy only once the server's first application
                    // event arrives. HTTP headers alone can leave the state poll
                    // demoted while no session subscription is actually receiving.
                    onConnectionChange?.(true, { reconnectCount, backoffMs: reconnectDelayMs });
                    onEvent(data, { reconnectCount, backoffMs: reconnectDelayMs });
                    reconnectCount = Math.min(reconnectCount + 1, 10000);
                    reconnectDelayMs = null;
                    retryMs = 1000;
                  } else {
                    onEvent(data);
                  }
                  if (data.event === "authorization_revoked") {
                    authorizationRevoked = true;
                  }
                } catch (e) {
                  // Ignore parse errors.
                }
              }
            }
          }
        }
        // Stream ended cleanly (server closed). Reconnect unless told to stop.
        onConnectionChange?.(false, {
          reason: authorizationRevoked ? "authorization_revoked" : "stream_ended",
          reconnectCount: Math.max(reconnectCount - 1, 0),
        });
        if (!signal.aborted && !authorizationRevoked) scheduleReconnect();
      })
      .catch(() => {
        // Connection lost (deploy, network blip). Reconnect unless told to stop.
        onConnectionChange?.(false, {
          reason: signal.aborted ? "aborted" : "network_error",
          reconnectCount: Math.max(reconnectCount - 1, 0),
        });
        if (!signal.aborted && !authorizationRevoked) scheduleReconnect();
      });
  }

  function scheduleReconnect() {
    // Exponential backoff with jitter: 0.5-1s, 1-2s, 2-4s, ..., capped at 30s.
    // A brief initial delay handles a fast Railway deploy; backoff prevents a
    // thundering herd if many devices reconnect after a prolonged outage.
    const delay = retryMs * (0.5 + Math.random() * 0.5);
    reconnectDelayMs = Math.round(delay);
    retryMs = Math.min(retryMs * 2, MAX_RETRY_MS);
    setTimeout(connect, delay);
  }

  connect();
}

/**
 * 202 means finalisation is still running; the caller polls rather than erroring.
 *
 * This used to sit in a `catch` testing `err.status === 202`, which never fired:
 * `Response.ok` is true for the whole 2xx range, so a 202 was returned as a ready
 * result whose body is `{detail: "…not ready yet"}`. `renderResult` then died on
 * `result.overlaps.length` and the poll loop stopped after a single attempt, so a
 * session that finalised perfectly well on the server never appeared in the app.
 * The status has to be read, not inferred from whether a throw happened.
 */
export async function getResult(id) {
  const { status, data } = await request("GET", `/v1/sessions/${id}/result`, undefined, {
    withStatus: true,
  });
  if (status === 202)
    return {
      ready: false,
      detail: data?.detail,
      degraded: data?.degraded,
      // The three typed fields the 202 grew for #286. Passed through rather than
      // interpreted: `session.ts` decides what a state means, and `undefined` here
      // is what an older server looks like -- which the caller must read as
      // *unknown*, never as a state.
      session_state: data?.session_state,
      pending: data?.pending,
      expires_at: data?.expires_at,
      // Where processing is (#355), passed through the same way: absent is unknown.
      processing_stage: data?.processing_stage,
    };
  return { ready: true, result: data };
}

/** The notes styles the server offers, and whether it writes notes at all (#774). */
export const getNoteStyles = () => request("GET", "/v1/notes/styles");

/** A meeting's notes in every style anyone has asked for, with the styles (#774). */
export const getNotes = (id) =>
  request("GET", `/v1/sessions/${id}/notes`, undefined, { retry: false });

/**
 * Ask for a meeting's notes in one style (#774): starts them, joins the ones being
 * written, or returns the ones already there. `update` writes them again after the
 * meeting was processed again. Answers with the style's notes either way (202 while they
 * are being written). No retry: a 429 here is the daily cap, which a retry cannot clear.
 */
export const askNotes = (id, style, { update = false } = {}) =>
  request(
    "POST",
    `/v1/sessions/${id}/notes/${encodeURIComponent(style)}${update ? "/update" : ""}`,
    undefined,
    { retry: false },
  );

/**
 * A meeting's saved action ticks (#786), for the actions its current result has. No
 * retry: the screen reads again on focus and on its own poll.
 */
export const getActionTicks = (id) =>
  request("GET", `/v1/sessions/${id}/action-ticks`, undefined, { retry: false });

/**
 * Tick or untick one action (#786): `{done, age_ms}`, the change's age on the page's
 * monotonic clock (never a wall-clock time). Answers the state that stands,
 * which may be a later change by someone else. No retry: the screen keeps a change that
 * got no answer and sends it again itself, and a refusal is rolled back.
 */
export const putActionTick = (id, key, body) =>
  request("PUT", `/v1/sessions/${id}/action-ticks/${encodeURIComponent(key)}`, body, { retry: false });

/** This person's own Ask AI chat about a meeting (#791), and whether they can ask now. */
export const getChat = (id) =>
  request("GET", `/v1/sessions/${id}/chat`, undefined, { retry: false });

/** Delete this person's Ask AI chat about a meeting (#791). */
export const deleteChat = (id) =>
  request("DELETE", `/v1/sessions/${id}/chat`, undefined, { retry: false });

/**
 * Ask a question about a meeting (#791). The answer streams back as server-sent events,
 * each handed to `onEvent` as it arrives (`accepted`, `scope`, `segment`, `done`, `error`).
 *
 * Not `request()`, on purpose: that retries a 429 or a 5xx and sleeps for `Retry-After`,
 * which here would ask a paid question twice, or wait until tomorrow on a daily cap. One
 * attempt; a refusal rejects with the server's `status`, its `code` (`detail.code`) and
 * any `retryAfter`. `signal` aborts the request, and the server then stops writing the
 * answer; an aborted call rejects with `aborted: true`.
 */
export async function askChat(id, question, { signal, onEvent } = {}) {
  const headers = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
    [CLIENT_HEADER_NAME]: clientHeader(),
    ...buildHeaderEntry(),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const aborted = () => {
    const e = new Error("request aborted");
    e.aborted = true;
    return e;
  };
  let res;
  try {
    res = await fetch(`/v1/sessions/${id}/chat`, {
      method: "POST",
      headers,
      body: JSON.stringify({ question }),
      signal,
    });
  } catch (err) {
    if (signal?.aborted) throw aborted();
    throw err;
  }
  if (!res.ok) {
    let data = null;
    try {
      data = await res.json();
    } catch {
      data = null;
    }
    const detail = data?.detail;
    const err = new Error(typeof detail?.message === "string" ? detail.message : res.statusText);
    err.status = res.status;
    err.code = typeof detail?.code === "string" ? detail.code : null;
    // A plan's Ask AI limit (#874): how many questions, and when Pro's month resets.
    if (typeof detail?.limit === "number") err.limit = detail.limit;
    if (typeof detail?.resets_at === "string") err.resetsAt = detail.resets_at;
    // And the asker's plan (null without one), for the next plan up (#1010).
    if (detail && "plan" in detail) err.plan = typeof detail.plan === "string" ? detail.plan : null;
    const after = Number(res.headers.get("Retry-After"));
    if (Number.isFinite(after) && after > 0) err.retryAfter = after;
    if (res.status === 401) onUnauthorized?.();
    throw err;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
      let end;
      while ((end = buffer.indexOf("\n\n")) >= 0) {
        const block = buffer.slice(0, end);
        buffer = buffer.slice(end + 2);
        const data = block
          .split("\n")
          .filter((line) => line.startsWith("data:"))
          .map((line) => line.slice(5).trimStart())
          .join("\n");
        if (!data) continue;
        let event;
        try {
          event = JSON.parse(data);
        } catch {
          continue;
        }
        onEvent?.(event);
      }
    }
  } catch (err) {
    if (signal?.aborted) throw aborted();
    throw err;
  }
}

/**
 * Run a failed session's processing again. Creator only, and the server decides:
 * a server without the route answers 404/405, which the caller reports as such.
 */
export const retryProcessing = (id) =>
  request("POST", `/v1/sessions/${id}/retry`, undefined, { retry: false });

/**
 * Delete this device's audio for a session, now.
 *
 * The server's sweep covers abandonment a browser cannot signal -- a killed app, a flat
 * battery, a closed laptop. This is for someone who actively changed their mind and
 * should not have to wait one out.
 */
export const discardAudio = (id) => request("DELETE", `/v1/sessions/${id}/audio`);

/**
 * Push subscriptions. The body shape is the transport-agnostic one the server
 * defines (`protocol.PushSubscribe`), not `PushSubscription.toJSON()` -- the
 * browser's shape nests the keys, and flattening here means a native client
 * later posts to this same endpoint with `transport: "fcm"` and no keys at all.
 */
export const subscribePush = (body) => request("POST", "/v1/push/subscriptions", body);
/**
 * `request` sends a body on DELETE, which is unusual but correct here: the thing
 * being deleted is identified by an endpoint URL far too long to put in a path.
 */
export const unsubscribePush = (body) => request("DELETE", "/v1/push/subscriptions", body);
